/**
 * Tiny SVG line chart, no dependency (Live tab recent power, 4 series
 * x ~100 points). Pure presentation: the parent owns polling + range.
 * Hover (desktop) and touch scrub (mobile) snap a crosshair to the
 * nearest index with a pinned tooltip; `touch-action: pan-y` keeps the
 * horizontal gesture for scrubbing while the page still scrolls.
 * Hand-rolled over uPlot/recharts for bundle size and CSS-var theming.
 * docs/customer-app.md > Front-end > Chart scrub
 */
import { useId, useRef, useState } from "react";

export interface ChartSeries {
  name: string;
  color: string;
  values: number[];     // aligned positionally with `t`
  /** Use ↓/↑ arrows in the tooltip instead of a plain magnitude.
   * Set this on series whose sign carries meaning (battery: + = charging
   * into node = ↓, − = discharging out of node = ↑; grid: + = importing
   * = ↓, − = exporting = ↑). Leave false for PV / load - those are
   * always one-directional so a leading sign was just visual noise. */
  directional?: boolean;
}

interface Props {
  t: number[];          // unix seconds, ascending
  series: ChartSeries[];
  height?: number;
  /** Render a 0 W reference line - useful because battery + grid go negative. */
  zeroLine?: boolean;
  /** "line" (default) draws a stroked path per series. "bar" draws one
   *  filled rect per data point, dropping to the zero baseline. Use
   *  "bar" for single-series PV / Load views where the "how much energy
   *  flowed in each interval" mental model fits the chart better than
   *  an instantaneous power curve. Multi-series + "bar" works but the
   *  bars stack at the same x-position rather than grouping, so it's
   *  best with a single series. */
  mode?: "line" | "bar";
  /** Forces yMin=0 and skips the bottom padding. Use for metrics that
   *  can't be negative (PV, half-wave-rectified battery_in / out etc.)
   *  so bars sit flush on the baseline instead of "floating" above a
   *  visible -250 W tick. */
  floorAtZero?: boolean;
  /** Optionele procentlijn (0-100) over het hele chartvlak, met een
   *  eigen onzichtbare as - de auto-SOC over de laadbalken heen.
   *  Gaten (null) breken de lijn: onbekende SOC is geen 0. */
  overlayPct?: { values: (number | null)[]; color: string } | null;
}

const VB_W = 600;
const PAD = { left: 40, right: 12, top: 8, bottom: 22 };
// Tooltip dimensions in viewBox units (NOT pixels - they scale with
// the chart). Chosen so 5 lines (time + 4 series) fit at fontSize 11.
const TT = { w: 154, lineH: 14, padX: 8, padY: 8 };

export function LineChart({ t, series, height = 200, zeroLine = true, mode = "line", floorAtZero = false, overlayPct = null }: Props) {
  const gradId = useId().replace(/:/g, "");
  const svgRef = useRef<SVGSVGElement>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);

  if (t.length === 0 || series.every((s) => s.values.length === 0)) {
    return (
      <div className="h-[200px] grid place-items-center text-ink-mute text-sm">
        geen data
      </div>
    );
  }

  // Y domain across all series; pad ±10 %; force 0 in when zeroLine is on.
  let yMin = Math.min(...series.flatMap((s) => s.values));
  let yMax = Math.max(...series.flatMap((s) => s.values));
  if (zeroLine) {
    yMin = Math.min(yMin, 0);
    yMax = Math.max(yMax, 0);
  }
  if (yMax - yMin < 100) {
    const mid = (yMax + yMin) / 2;
    yMin = mid - 100;
    yMax = mid + 100;
  }
  const yPad = (yMax - yMin) * 0.1;
  // Top always pads so the highest value isn't pressed against the
  // chart edge. Bottom pads UNLESS the caller asked to floor at 0
  // (i.e. the metric can't be negative - PV / load / etc.). Bars then
  // sit flush on the baseline rather than floating above a "-250 W" tick.
  yMax += yPad;
  if (floorAtZero) {
    yMin = 0;
  } else {
    yMin -= yPad;
  }

  const tx = (i: number) =>
    PAD.left + (i / Math.max(1, t.length - 1)) * (VB_W - PAD.left - PAD.right);
  const ty = (v: number) => {
    const norm = (v - yMin) / (yMax - yMin);
    return height - PAD.bottom - norm * (height - PAD.top - PAD.bottom);
  };

  const yTicks = [yMin, (yMin + yMax) / 2, yMax].map((v) => Math.round(v));
  const tickCount = Math.min(5, t.length);
  const tickIdx = Array.from({ length: tickCount }, (_, k) =>
    Math.round((k / (tickCount - 1 || 1)) * (t.length - 1)),
  );

  /** Map a client (screen) x-coordinate to the nearest data index.
   * Uses the SVG's CTM to invert client coords into viewBox coords -
   * stays correct as the chart resizes. */
  function pointerToIndex(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vbX = pt.matrixTransform(ctm.inverse()).x;
    // Clamp to chart area then find nearest tx(i).
    const x = Math.max(PAD.left, Math.min(VB_W - PAD.right, vbX));
    const usable = VB_W - PAD.left - PAD.right;
    const frac = (x - PAD.left) / usable;
    return Math.round(frac * (t.length - 1));
  }

  // Subtle haptic on TOUCH scrub: 3 ms tick per snapped-index change.
  // Tracks the last vibrated index in a ref so a single touch-drag
  // produces one tick per data point, not one per pointermove. Mouse
  // events skip this - desktop has no motor + Apple's iPadOS treats
  // vibrate() as no-op anyway, but we still avoid the wasted call.
  // Each successive navigator.vibrate() cancels the previous, so even
  // a fast scrub through dozens of indices doesn't queue up audible
  // buzzing.
  const lastVibratedIdx = useRef<number | null>(null);

  const setFromClient = (clientX: number, isTouch = false) => {
    const idx = pointerToIndex(clientX);
    if (idx === null) return;
    setActiveIdx(idx);
    if (isTouch && idx !== lastVibratedIdx.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibratedIdx.current = idx;
    }
  };
  const clear = () => {
    setActiveIdx(null);
    lastVibratedIdx.current = null;
  };

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${height}`}
      className="w-full h-auto select-none"
      role="img"
      aria-label={`Grafiek: ${series.map((s) => s.name).join(", ")}`}
      preserveAspectRatio="none"
      // `touch-action: pan-y` keeps the chart's horizontal swipe for
      // scrubbing while the page can still scroll vertically.
      // `data-swipe-consume` tells the app-level useSwipeNav() hook
      // to ignore touches that start in here, so a chart scrub never
      // accidentally switches tabs.
      style={{ touchAction: "pan-y" }}
      data-swipe-consume="true"
      onMouseMove={(e) => setFromClient(e.clientX, false)}
      onMouseLeave={clear}
      onTouchStart={(e) => setFromClient(e.touches[0].clientX, true)}
      onTouchMove={(e) => setFromClient(e.touches[0].clientX, true)}
      onTouchEnd={clear}
      onTouchCancel={clear}
    >
      {/* Y-axis grid + labels */}
      {yTicks.map((v, i) => (
        <g key={`y${i}`}>
          <line
            x1={PAD.left}
            x2={VB_W - PAD.right}
            y1={ty(v)}
            y2={ty(v)}
            stroke="var(--color-line)"
            strokeWidth={1}
          />
          <text
            x={PAD.left - 4}
            y={ty(v) + 3}
            textAnchor="end"
            fontSize={10}
            fill="var(--color-ink-mute)"
            className="tabular"
          >
            {formatYTick(v)}
          </text>
        </g>
      ))}

      {/* Percentage-overlay (auto-SOC): eigen 0-100-as over het hele
          vlak, gestreept zodat hij nooit met de vermogensreeksen
          verward wordt. */}
      {overlayPct && (() => {
        const yPct = (v: number) =>
          PAD.top + (1 - v / 100) * (height - PAD.top - PAD.bottom);
        let d = "";
        let pen = false;
        t.forEach((_ts, i) => {
          const v = overlayPct.values[i];
          if (v == null) { pen = false; return; }
          const cmd = pen ? "L" : "M";
          d += ` ${cmd} ${tx(i).toFixed(1)} ${yPct(v).toFixed(1)}`;
          pen = true;
        });
        if (!d) return null;
        const last = [...overlayPct.values].reverse().find((v) => v != null);
        return (
          <g>
            <path d={d} fill="none" stroke={overlayPct.color} strokeWidth={2}
                  strokeDasharray="5 4" strokeLinejoin="round" opacity={0.9} />
            {last != null && (
              <text x={VB_W - PAD.right} y={yPct(last as number) - 5}
                    textAnchor="end" fontSize={10} fill={overlayPct.color}
                    className="tabular">
                {Math.round(last as number)}%
              </text>
            )}
          </g>
        );
      })()}

      {/* Zero line emphasized */}
      {zeroLine && yMin < 0 && yMax > 0 && (
        <line
          x1={PAD.left}
          x2={VB_W - PAD.right}
          y1={ty(0)}
          y2={ty(0)}
          stroke="var(--color-ink-mute)"
          strokeWidth={1}
          strokeDasharray="2 3"
          opacity={0.6}
        />
      )}

      {/* Gradient defs - one per series, keyed by index so ids stay
          unique when multiple charts render on one page. */}
      {mode === "line" && (
        <defs>
          {series.map((s, si) => (
            <linearGradient key={s.name} id={`${gradId}-${si}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={s.color} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
      )}

      {/* Series - line OR bar mode. Same underlying values. */}
      {mode === "line"
        ? series.map((s, si) => {
            const line = s.values
              .map((v, i) => `${i === 0 ? "M" : "L"}${tx(i).toFixed(2)} ${ty(v).toFixed(2)}`)
              .join(" ");
            const baseY = ty(Math.max(0, yMin));
            const area = `${line} L${tx(s.values.length - 1).toFixed(2)} ${baseY.toFixed(2)} L${tx(0).toFixed(2)} ${baseY.toFixed(2)} Z`;
            return (
              <g key={s.name}>
                <path d={area} fill={`url(#${gradId}-${si})`} stroke="none" />
                <path
                  d={line}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={2.25}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            );
          })
        : (() => {
            // Bar width - leave a small gap between bars for legibility.
            // With dense series (e.g. 120 points on a 24h step=5m chart)
            // the gap evaporates and we get a packed histogram look,
            // which is exactly right.
            const usable = VB_W - PAD.left - PAD.right;
            const slotW = usable / Math.max(1, t.length);
            const barW = Math.max(0.5, slotW * 0.86);
            // Anchor bars to the zero baseline so positive bars rise
            // from 0 and (if zeroLine on) negative bars hang below.
            const y0 = ty(0);
            return series.map((s) =>
              s.values.map((v, i) => {
                const x = tx(i) - barW / 2;
                const top = v >= 0 ? ty(v) : y0;
                const h = Math.max(0.5, Math.abs(ty(v) - y0));
                return (
                  <rect
                    key={`${s.name}-${i}`}
                    x={x}
                    y={top}
                    width={barW}
                    height={h}
                    fill={s.color}
                    opacity={0.8}
                    rx={2}
                  />
                );
              }),
            );
          })()}

      {/* X-axis labels */}
      {tickIdx.map((i) => (
        <text
          key={`x${i}`}
          x={tx(i)}
          y={height - 4}
          textAnchor={i === 0 ? "start" : i === t.length - 1 ? "end" : "middle"}
          fontSize={10}
          fill="var(--color-ink-mute)"
          className="tabular"
        >
          {formatXTick(t[i])}
        </text>
      ))}

      {/* Crosshair + tooltip */}
      {activeIdx !== null && (
        <Crosshair
          idx={activeIdx}
          t={t}
          series={series}
          tx={tx}
          ty={ty}
          height={height}
        />
      )}
    </svg>
  );
}

interface CrosshairProps {
  idx: number;
  t: number[];
  series: ChartSeries[];
  tx: (i: number) => number;
  ty: (v: number) => number;
  height: number;
}

function Crosshair({ idx, t, series, tx, ty, height }: CrosshairProps) {
  const x = tx(idx);
  // Place tooltip on the opposite side of the cursor so it doesn't
  // hide what the user is pointing at. Switch sides past mid-chart.
  const placeLeft = x + TT.w + 12 > VB_W;
  const ttX = placeLeft ? x - TT.w - 8 : x + 8;
  const ttY = PAD.top;
  const ttH = TT.padY * 2 + TT.lineH * (1 + series.length);
  const timeLabel = formatTimeFull(t[idx]);

  return (
    <g pointerEvents="none">
      {/* Vertical line at cursor */}
      <line
        x1={x}
        x2={x}
        y1={PAD.top}
        y2={height - PAD.bottom}
        stroke="var(--color-ink-mute)"
        strokeWidth={1}
        strokeDasharray="3 3"
        opacity={0.7}
      />
      {/* Dot per series at the cursor */}
      {series.map((s) => {
        const v = s.values[idx];
        if (v === undefined) return null;
        return (
          <circle
            key={s.name}
            cx={x}
            cy={ty(v)}
            r={3.5}
            fill="var(--color-card)"
            stroke={s.color}
            strokeWidth={2}
          />
        );
      })}

      {/* Tooltip */}
      <g transform={`translate(${ttX}, ${ttY})`}>
        <rect
          width={TT.w}
          height={ttH}
          rx={8}
          fill="var(--color-card)"
          stroke="var(--color-line)"
          strokeWidth={1}
        />
        <text
          x={TT.padX}
          y={TT.padY + 10}
          fontSize={11}
          fill="var(--color-ink-mute)"
          className="tabular"
        >
          {timeLabel}
        </text>
        {series.map((s, i) => {
          const v = s.values[idx] ?? 0;
          const y = TT.padY + TT.lineH * (i + 1) + 10;
          return (
            <g key={s.name}>
              <circle cx={TT.padX + 4} cy={y - 3} r={3} fill={s.color} />
              <text
                x={TT.padX + 14}
                y={y}
                fontSize={11}
                fill="var(--color-ink)"
              >
                {s.name}
              </text>
              <text
                x={TT.w - TT.padX}
                y={y}
                fontSize={11}
                fontWeight={600}
                fill={s.color}
                textAnchor="end"
                className="tabular"
              >
                {formatTooltipValue(v, s.directional)}
              </text>
            </g>
          );
        })}
      </g>
    </g>
  );
}

/** kW for ≥1000 W; raw W otherwise. */
function formatYTick(w: number): string {
  if (Math.abs(w) >= 1000) return `${(w / 1000).toFixed(1)} kW`;
  return `${w} W`;
}

/** Tooltip value formatter: directional series (battery, grid) get
 * the same arrow vocabulary as the energy-flow card (↓ into the node,
 * ↑ out of it); non-directional series (PV, load) show the magnitude
 * alone, a leading + was visual noise.
 * docs/customer-app.md > Front-end > Chart scrub
 */
function formatTooltipValue(w: number, directional: boolean | undefined): string {
  const mag = Math.abs(w);
  const num = mag >= 1000
    ? `${(mag / 1000).toFixed(2)} kW`
    : `${Math.round(mag)} W`;
  if (!directional) return num;
  const arrow = w > 0 ? "↓" : w < 0 ? "↑" : "·";
  return `${arrow} ${num}`;
}

const HM = new Intl.DateTimeFormat("nl-NL", { hour: "2-digit", minute: "2-digit" });
const HMS = new Intl.DateTimeFormat("nl-NL", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});
function formatXTick(unixSec: number): string {
  return HM.format(new Date(unixSec * 1000));
}
/** Tooltip uses HH:MM:SS so successive samples in a 15m window are
 * distinguishable; the X-axis labels stay HH:MM to keep them short. */
function formatTimeFull(unixSec: number): string {
  return HMS.format(new Date(unixSec * 1000));
}
